import { z } from 'zod';

import { type ValidationMessageSet } from '../../form-validations';

export type NameInputs = { firstName: string; lastName: string };

export type NameValidationMessages = Pick<
  ValidationMessageSet,
  'lengthMessage' | 'requiredMessage'
>;

const FIRST_NAME_MAX = 40;
const LAST_NAME_MAX = 80;

// Validation shared between client form & API payload
// Messages aren't needed for API payload validation
export const getBaseSchema = (
  firstName?: NameValidationMessages,
  lastName?: NameValidationMessages
) =>
  z.object({
    firstName: z
      .string({ required_error: firstName?.requiredMessage })
      .trim()
      .min(2, firstName?.requiredMessage)
      .max(
        FIRST_NAME_MAX,
        firstName?.lengthMessage.replace('{max}', String(FIRST_NAME_MAX))
      ),
    lastName: z
      .string({ required_error: lastName?.requiredMessage })
      .trim()
      .min(2, lastName?.requiredMessage)
      .max(
        LAST_NAME_MAX,
        lastName?.lengthMessage.replace('{max}', String(LAST_NAME_MAX))
      ),
  });

const checkForHtml = (str: string): boolean => {
  const dom = new DOMParser();

  const emptySelection = dom
    .parseFromString('', 'text/html')
    .querySelectorAll('*');
  const parsedSelection = dom
    .parseFromString(str, 'text/html')
    .querySelectorAll('*');

  return emptySelection.length === parsedSelection.length;
};

// Validation for client form only
export const getNameSchema = (
  firstName: NameValidationMessages,
  lastName: NameValidationMessages
) => {
  const { shape } = getBaseSchema(firstName, lastName);

  return z.object({
    firstName: shape.firstName.refine(checkForHtml),
    lastName: shape.lastName.refine(checkForHtml),
  });
};
